import { useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import PostCard from "@/components/feature/PostCard";
import { useDocumentMeta, SITE_ORIGIN } from "@/hooks/useDocumentMeta";
import { communityPosts, hotTags, type CommunityReply } from "@/mocks/posts";
import { formatCount } from "@/utils/format";
import ReplyThread from "./components/ReplyThread";

export default function PostDetail() {
  const { id } = useParams<{ id: string }>();
  const post = communityPosts.find((item) => item.id === id);

  const [liked, setLiked] = useState(false);
  const [likes, setLikes] = useState(post?.likes ?? 0);
  const [saved, setSaved] = useState(false);
  const [toast, setToast] = useState("");
  const [draft, setDraft] = useState("");
  const [myReplies, setMyReplies] = useState<CommunityReply[]>([]);

  const related = useMemo(
    () => communityPosts.filter((item) => item.id !== id).slice(0, 3),
    [id],
  );

  const path = `/post/${id ?? ""}`;
  useDocumentMeta({
    title: post
      ? `${post.title} - 黑料网社热帖`
      : "帖子未找到 - 黑料网社",
    description: post
      ? `${post.summary.slice(0, 90)}…黑料网社热帖专区，附高赞神回复与加精置顶热评。`
      : "该帖子可能已被移除或链接有误，去黑料网社热帖榜看看今天正在围观什么。",
    keywords: post
      ? `${post.category},黑料网社热帖,黑料网社神回复,${post.tags.join(",")}`
      : "黑料网社,热帖榜,神回复",
    path,
    ogType: "article",
    jsonLd: post
      ? [
          {
            "@context": "https://schema.org",
            "@type": "Article",
            headline: post.title,
            description: post.summary,
            articleSection: post.category,
            inLanguage: "zh-CN",
            author: { "@type": "Person", name: post.author },
            publisher: { "@type": "Organization", name: "黑料网社" },
            mainEntityOfPage: `${SITE_ORIGIN}${path}`,
            interactionStatistic: {
              "@type": "InteractionCounter",
              interactionType: "https://schema.org/LikeAction",
              userInteractionCount: post.likes,
            },
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "首页", item: `${SITE_ORIGIN}/` },
              { "@type": "ListItem", position: 2, name: "热帖榜", item: `${SITE_ORIGIN}/hot` },
              { "@type": "ListItem", position: 3, name: post.title, item: `${SITE_ORIGIN}${path}` },
            ],
          },
        ]
      : undefined,
  });

  function showToast(message: string) {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  }

  function toggleLike() {
    setLiked((prev) => {
      setLikes((count) => (prev ? count - 1 : count + 1));
      return !prev;
    });
  }

  async function handleShare() {
    const url = `${SITE_ORIGIN}${path}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制，快去分享给围观群众");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  }

  function submitReply() {
    const text = draft.trim();
    if (!text) {
      showToast("先写两句再发表吧");
      return;
    }
    setMyReplies((prev) => [
      {
        id: `my-${Date.now()}`,
        author: "我",
        role: "围观群众",
        time: "刚刚",
        content: text,
        likes: 0,
        essence: false,
      },
      ...prev,
    ]);
    setDraft("");
    showToast("已发表，坐等神回复");
  }

  if (!post) {
    return (
      <SiteLayout>
        <section className="w-full bg-background-50 pt-24 md:pt-32 pb-20">
          <div className="max-w-content mx-auto px-4 md:px-8 text-center">
            <span className="w-16 h-16 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
              <i className="ri-ghost-line text-3xl"></i>
            </span>
            <h1 className="mt-6 font-heading text-2xl md:text-3xl font-bold text-foreground-950">
              这个帖子好像被收走了
            </h1>
            <p className="mt-3 text-sm md:text-base text-foreground-600">
              链接可能有误，或者内容已经下架。去热帖榜看看今天正在围观什么吧。
            </p>
            <Link
              to="/hot"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              <i className="ri-fire-line"></i>
              回到热帖榜
            </Link>
          </div>
        </section>
      </SiteLayout>
    );
  }

  return (
    <SiteLayout>
      <article>
        <header className="w-full bg-foreground-950 pt-24 md:pt-28 pb-10 md:pb-14 paper-grain">
          <div className="max-w-content mx-auto px-4 md:px-8">
            <div className="[&_a]:text-background-300 [&_a:hover]:text-primary-200 [&_span]:text-background-200 [&_.ri-arrow-right-s-line]:text-background-500">
              <Breadcrumbs
                items={[
                  { label: "首页", to: "/" },
                  { label: "热帖榜", to: "/hot" },
                  { label: post.category, to: `/hot?category=${post.categorySlug}` },
                ]}
              />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <span className="px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-xs font-medium">
                {post.category}
              </span>
              {post.essence && (
                <span className="px-2.5 py-1 rounded-md bg-accent-500 text-background-50 text-xs font-medium">
                  加精
                </span>
              )}
              {post.pinned && (
                <span className="px-2.5 py-1 rounded-md bg-secondary-500 text-background-50 text-xs font-medium">
                  置顶
                </span>
              )}
            </div>

            <h1 className="mt-5 font-heading text-2xl md:text-4xl lg:text-[2.75rem] font-black text-background-50 leading-snug max-w-4xl">
              {post.title}
            </h1>

            <div className="mt-6 flex flex-wrap items-center gap-x-6 gap-y-3 text-sm text-background-300">
              <span className="inline-flex items-center gap-2.5">
                <span className="w-8 h-8 rounded-full bg-primary-500 text-background-50 flex items-center justify-center text-xs font-medium">
                  {post.author.slice(0, 1)}
                </span>
                <span className="text-background-100">{post.author}</span>
                <span className="px-2 py-0.5 rounded-md bg-background-50/10 text-[11px]">
                  {post.authorTitle}
                </span>
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                {post.time}
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-fire-fill text-primary-400"></i>
                热度 {formatCount(post.heat)}
              </span>
            </div>
          </div>
        </header>

        <div className="w-full bg-background-50">
          <div className="max-w-content mx-auto px-4 md:px-8 py-12 md:py-16">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
              <div className="lg:col-span-8">
                <figure className="rounded-lg overflow-hidden border border-background-200">
                  <span className="block h-60 md:h-[420px] w-full">
                    <img
                      src={post.cover}
                      alt={post.coverAlt}
                      className="w-full h-full object-cover object-top"
                    />
                  </span>
                  <figcaption className="px-5 py-3 text-xs text-foreground-500 bg-background-100 border-t border-background-200">
                    社区图片 · {post.coverAlt}
                  </figcaption>
                </figure>

                <div className="mt-8 space-y-5">
                  {post.content.map((paragraph, index) => (
                    <p
                      key={index}
                      className="text-sm md:text-base text-foreground-800 leading-[1.9]"
                    >
                      {paragraph}
                    </p>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-2">
                  {post.tags.map((tag) => (
                    <Link
                      key={tag}
                      to={`/hot?q=${encodeURIComponent(tag)}`}
                      className="px-3 py-1.5 rounded-full text-xs bg-secondary-100 text-secondary-800 hover:bg-secondary-200 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      #{tag}
                    </Link>
                  ))}
                </div>

                <div className="mt-8 flex flex-wrap items-center gap-3 pt-6 border-t border-background-200">
                  <button
                    type="button"
                    onClick={toggleLike}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      liked
                        ? "bg-accent-500 text-background-50"
                        : "bg-background-100 text-foreground-800 border border-background-200 hover:border-accent-300 hover:text-accent-700"
                    }`}
                  >
                    <i className={liked ? "ri-thumb-up-fill" : "ri-thumb-up-line"}></i>
                    {formatCount(likes)} 点赞
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setSaved((v) => !v);
                      showToast(saved ? "已取消收藏" : "已收藏，稍后接着看");
                    }}
                    className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      saved
                        ? "bg-secondary-500 text-background-50"
                        : "bg-background-100 text-foreground-800 border border-background-200 hover:border-secondary-300 hover:text-secondary-700"
                    }`}
                  >
                    <i className={saved ? "ri-bookmark-fill" : "ri-bookmark-line"}></i>
                    {saved ? "已收藏" : "收藏"}
                  </button>
                  <button
                    type="button"
                    onClick={handleShare}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md text-sm font-medium bg-background-100 text-foreground-800 border border-background-200 hover:border-primary-300 hover:text-primary-700 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-share-forward-line"></i>
                    分享
                  </button>
                  {toast && (
                    <span className="text-xs text-primary-700 animate-fade-up">{toast}</span>
                  )}
                </div>

                <section className="mt-12" aria-labelledby="replies-title">
                  <div className="flex items-end justify-between gap-3">
                    <h2
                      id="replies-title"
                      className="font-heading text-xl md:text-2xl font-bold text-foreground-950"
                    >
                      热评区 · {formatCount(post.replyCount + myReplies.length)} 条讨论
                    </h2>
                    <span className="text-xs text-foreground-500 whitespace-nowrap">
                      金句频出，理性围观
                    </span>
                  </div>

                  <div className="mt-6 p-5 rounded-lg bg-background-100 border border-background-200">
                    <label htmlFor="reply-input" className="block text-sm text-foreground-700 mb-2">
                      我也说两句
                    </label>
                    <textarea
                      id="reply-input"
                      value={draft}
                      maxLength={500}
                      onChange={(e) => setDraft(e.target.value)}
                      rows={3}
                      placeholder="围观有风险，发言需谨慎。写下你的神回复…"
                      className="w-full px-4 py-3 rounded-md bg-background-50 border border-background-200 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200 transition-colors resize-none"
                    />
                    <div className="mt-3 flex items-center justify-between gap-3">
                      <span className="text-xs text-foreground-400">{draft.length}/500</span>
                      <button
                        type="button"
                        onClick={submitReply}
                        className="px-5 py-2.5 rounded-md text-sm font-medium bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        发表围观
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    {myReplies.map((reply) => (
                      <ReplyThread key={reply.id} reply={reply} />
                    ))}
                    {post.replies.map((reply) => (
                      <ReplyThread key={reply.id} reply={reply} />
                    ))}
                  </div>
                </section>
              </div>

              <aside className="lg:col-span-4">
                <div className="lg:sticky lg:top-24 space-y-6">
                  <div className="p-5 md:p-6 rounded-lg bg-background-100 border border-background-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      关于作者
                    </h3>
                    <div className="mt-4 flex items-center gap-3">
                      <span className="w-12 h-12 rounded-full bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg font-bold">
                        {post.author.slice(0, 1)}
                      </span>
                      <span>
                        <span className="block text-sm font-medium text-foreground-900">
                          {post.author}
                        </span>
                        <span className="block text-xs text-foreground-500">
                          {post.authorTitle}
                        </span>
                      </span>
                    </div>
                    <p className="mt-4 text-sm text-foreground-600 leading-relaxed">
                      长期活跃在{post.category}板块，擅长把日常小事写成值得围观的故事。
                    </p>
                  </div>

                  <div className="p-5 md:p-6 rounded-lg bg-background-50 border border-background-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      相关推荐
                    </h3>
                    <div className="mt-2">
                      {related.map((item) => (
                        <PostCard key={item.id} post={item} variant="compact" />
                      ))}
                    </div>
                  </div>

                  <div className="p-5 md:p-6 rounded-lg bg-primary-50 border border-primary-200">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      热门标签
                    </h3>
                    <div className="mt-4 flex flex-wrap gap-2">
                      {hotTags.map((tag) => (
                        <Link
                          key={tag}
                          to={`/hot?q=${encodeURIComponent(tag)}`}
                          className="px-3 py-1.5 rounded-full text-xs bg-background-50 border border-primary-200 text-primary-800 hover:bg-primary-100 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          {tag}
                        </Link>
                      ))}
                    </div>
                  </div>
                </div>
              </aside>
            </div>
          </div>
        </div>
      </article>
    </SiteLayout>
  );
}